export type LeaderboardEntry = {
  id: string;
  name: string;
  seed: number;
  difficulty: string;
  elapsedMs: number;
  createdAt: string;
};

export type ScoreInput = {
  name: string;
  seed: number;
  difficulty: string;
  elapsedMs: number;
};

const localStorageKey = "labyrinthLeaderboardEntries";
const maxNameLength = 14;
const allowedDifficulty = new Set(["easy", "medium", "hard"]);

export class LeaderboardClient {
  private readonly apiUrl = resolveApiUrl();
  private readonly allowLocalFallback = viteEnv().VITE_LEADERBOARD_LOCAL_FALLBACK === "1";

  apiBaseUrl(): string {
    return this.apiUrl;
  }

  async listTop(limit = 10, difficulty?: string): Promise<LeaderboardEntry[]> {
    if (!this.apiUrl || this.apiUrl === "local") {
      return this.filterDifficulty(this.localEntries(), difficulty)
        .sort(scoreSort)
        .slice(0, limit);
    }
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (difficulty) params.set("difficulty", difficulty);
      const response = await fetch(`${this.apiUrl}/api/scores?${params}`);
      if (!response.ok) throw new Error(`Leaderboard API failed: ${response.status}`);
      const data = (await response.json()) as { entries?: LeaderboardEntry[] };
      return Array.isArray(data.entries) ? data.entries : [];
    } catch {
      if (!this.allowLocalFallback) return [];
      return this.filterDifficulty(this.localEntries(), difficulty)
        .sort(scoreSort)
        .slice(0, limit);
    }
  }

  async submit(input: ScoreInput): Promise<LeaderboardEntry> {
    const normalizedDifficulty = normalizeDifficulty(input.difficulty);
    const payload = {
      ...input,
      name: normalizeName(input.name),
      difficulty: normalizedDifficulty,
      seed: clampInteger(input.seed, 1, 2_147_483_647),
      elapsedMs: Math.max(0, Math.floor(input.elapsedMs)),
    };
    if (this.apiUrl) {
      try {
        const response = await fetch(`${this.apiUrl}/api/scores`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`leaderboard_api_failed_${response.status}`);
        const data = (await response.json()) as { entry?: LeaderboardEntry };
        if (data.entry) return data.entry;
      } catch {
        if (!this.allowLocalFallback) {
          throw new Error("leaderboard_api_unreachable");
        }
      }
    }

    const entry: LeaderboardEntry = {
      id: crypto.randomUUID(),
      name: payload.name,
      seed: payload.seed,
      difficulty: payload.difficulty,
      elapsedMs: payload.elapsedMs,
      createdAt: new Date().toISOString(),
    };
    const entries = [...this.localEntries(), entry].sort(scoreSort);
    localStorage.setItem(localStorageKey, JSON.stringify(entries));
    return entry;
  }

  private localEntries(): LeaderboardEntry[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(localStorageKey) || "[]") as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isEntry);
    } catch {
      return [];
    }
  }

  private filterDifficulty(entries: LeaderboardEntry[], difficulty: string | undefined): LeaderboardEntry[] {
    return difficulty ? entries.filter((entry) => entry.difficulty === difficulty) : entries;
  }
}

function viteEnv(): Record<string, string | undefined> {
  return ((import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {});
}

function resolveApiUrl(): string {
  const configured = (viteEnv().VITE_LEADERBOARD_API_URL || "").trim().replace(/\/$/, "");
  if (configured) return configured;
  if (typeof window !== "undefined" && /^https?:/.test(window.location.origin)) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "local";
}

export function formatTime(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenth = Math.floor((elapsedMs % 1000) / 100);
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${tenth}`;
}

function normalizeName(name: string): string {
  const trimmed = name.trim().slice(0, maxNameLength);
  const cleaned = trimmed.replace(/[^\p{L}\p{N} _.-]/gu, "");
  return cleaned || "Unbekannt";
}

function normalizeDifficulty(value: string): string {
  const normalized = String(value ?? "").trim().toLowerCase();
  return allowedDifficulty.has(normalized) ? normalized : "medium";
}

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  const floored = Math.floor(value);
  if (floored < min) return min;
  if (floored > max) return max;
  return floored;
}

function isEntry(value: unknown): value is LeaderboardEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" &&
    typeof entry.name === "string" &&
    typeof entry.seed === "number" &&
    typeof entry.difficulty === "string" &&
    typeof entry.elapsedMs === "number" &&
    typeof entry.createdAt === "string"
  );
}

function scoreSort(a: LeaderboardEntry, b: LeaderboardEntry): number {
  return a.elapsedMs - b.elapsedMs || a.createdAt.localeCompare(b.createdAt);
}
